/**
 * useCobrowseSupport — Hook que gestiona el ciclo de vida completo de una
 * sesión co-browsing desde el lado del cliente:
 *
 *  1. El usuario escribe el detalle de su caso y pulsa "Pedir soporte" →
 *     requestSupport(reason) — el detalle viaja en la conexión inicial (ver
 *     CobrowseSession/eco_events) para que el agente lo vea sin preguntar.
 *  2. Se pide un token al propio backend (GET /cobrowse/token) y se abre la
 *     sesión WS en eco_events; el hub publica en Redis (→ CRM)
 *  3. Cuando el agente del CRM se une → onSessionReady → arranca la llamada
 *     de audio (WebRTC, cliente = no-initiator). El usuario comparte
 *     pantalla con `shareScreen()` cuando quiera (o el agente se lo pide
 *     por voz/chat) — es una acción manual, getDisplayMedia exige un gesto
 *     reciente del usuario, no se puede automatizar al unirse el agente.
 *  4. El usuario o el agente finaliza → endSession()
 *
 * Ex-rrweb: este hook usaba rrweb (mirror de DOM) para que el agente viera
 * la pantalla — se reemplazó por WebRTC screen-share real (video de
 * píxeles) tras un bug irresoluble de rrweb donde dashboards pesados
 * quedaban en blanco en el visor del agente pese a que el DOM reconstruido
 * era correcto (confirmado exhaustivamente: datos crudos correctos, DOM
 * reconstruido correcto, pero Chrome nunca llegaba a pintarlo — ni con
 * capturas nativas de DevTools, en ningún navegador/máquina). Con video no
 * hay reconstrucción de DOM que pueda fallar así.
 *
 * Control remoto: ENCENDIDO por defecto desde que arranca la sesión (se
 * avisa al agente al toque en onSessionReady, sin esperar ninguna acción
 * del usuario) — así el agente puede empezar a asistir de inmediato en
 * cuanto haya screen-share. El usuario lo puede apagar en cualquier momento
 * con `toggleRemoteControl()` — el gate se revisa en CADA `agent_control`
 * entrante (ver `remote-control.ts`), así que apagarlo a mitad de sesión
 * corta el control al toque. Cámara, micrófono y pantalla arrancan SIEMPRE
 * apagados (ver `webrtc-call.ts`) — la privacidad de audio/video/pantalla es
 * responsabilidad exclusiva del usuario, nunca se asume.
 *
 * Ver modules/cobrowse/cobrowse.controller.ts (apps/api) — ese endpoint
 * mintea el token con system: 'ecocont_tax' fijo.
 *
 * Permisos de mic/cámara: `micPermissionHint` se consulta (sin disparar el
 * prompt del navegador) al arrancar la sesión, para que la UI pueda avisar
 * de antemano "te vamos a pedir el micrófono" SOLO si todavía no está
 * concedido — nunca si ya lo está. Si el usuario intenta activar el
 * micrófono/cámara y el permiso está realmente denegado, `micBlocked`/
 * `cameraBlocked` se prenden para que la UI muestre instrucciones de cómo
 * habilitarlo a mano — `toggleMute()`/`enableVideo()` funcionan como
 * "reintentar" (vuelven a pedir el permiso) cuando ya estaban bloqueados.
 *
 * Persistencia/reconexión: el `sessionId` se guarda en `sessionStorage`
 * mientras la sesión está en curso — si el usuario recarga la página a
 * mitad de una sesión de soporte, al montar el hook de nuevo se detecta ese
 * `sessionId` persistido y se reconecta AUTOMÁTICAMENTE a la misma sesión
 * (pidiendo un token fresco, pero reusando el mismo id). Del lado de
 * eco_events, un corte de WS no mata la sesión de inmediato — hay un
 * período de gracia (~45s) durante el cual el agente solo ve "cliente
 * desconectado", así que una recarga normal (unos segundos) retoma la
 * sesión sin que el agente pierda nada. Si el período de gracia ya venció,
 * esto simplemente termina pidiendo soporte de nuevo — degradación
 * aceptable, nunca un error visible para el usuario.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { api } from '@/lib/api'
import { CobrowseSession } from '@/lib/cobrowsing/cobrowse-session'
import type { CobrowseStatus } from '@/lib/cobrowsing/cobrowse-session'
import { createRemoteControlHandler } from '@/lib/cobrowsing/remote-control'
import {
  createWebRTCCall,
  fetchTurnCredentials,
  queryMediaPermission,
  type MediaPermissionState,
  type WebRTCCall,
} from '@/lib/cobrowsing/webrtc-call'

/** Geometría de la ventana del navegador — reportada al agente cada
 * GEOMETRY_REPORT_MS mientras se comparte pantalla (ver sendScreenGeometry
 * en CobrowseSession) para que pueda traducir un clic sobre el video de
 * pantalla completa a coordenadas del viewport real de esta página. */
function currentWindowGeometry() {
  return {
    screenX: window.screenX,
    screenY: window.screenY,
    outerWidth: window.outerWidth,
    outerHeight: window.outerHeight,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
  }
}
const GEOMETRY_REPORT_MS = 500

interface CobrowseTokenResponse {
  token: string
  wsUrl: string
}

const PERSIST_KEY = 'cobrowse_session'

function persistSession(sessionId: string): void {
  try {
    sessionStorage.setItem(PERSIST_KEY, sessionId)
  } catch {
    // sessionStorage no disponible (modo privado estricto, etc.) — sin
    // persistencia, una recarga simplemente no auto-reconecta, no rompe nada.
  }
}

function readPersistedSession(): string | null {
  try {
    return sessionStorage.getItem(PERSIST_KEY)
  } catch {
    return null
  }
}

function clearPersistedSession(): void {
  try {
    sessionStorage.removeItem(PERSIST_KEY)
  } catch {
    // ignore
  }
}

export function useCobrowseSupport() {
  const [status, setStatus] = useState<CobrowseStatus>('idle')
  const [remoteControlEnabled, setRemoteControlEnabled] = useState(true)
  const [muted, setMuted] = useState(true)
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null)
  // El AGENTE (no este cliente) se desconectó transitoriamente — la sesión
  // sigue activa del lado de eco_events, solo informativo.
  const [agentDisconnected, setAgentDisconnected] = useState(false)
  // Estado del permiso de mic ANTES de pedirlo (solo para la guía preventiva).
  const [micPermissionHint, setMicPermissionHint] = useState<MediaPermissionState | null>(null)
  // true = el usuario intentó activar mic/cámara y el navegador lo rechazó
  // de verdad (no simplemente "todavía no lo pedimos").
  const [micBlocked, setMicBlocked] = useState(false)
  const [cameraBlocked, setCameraBlocked] = useState(false)
  // true = getDisplayMedia ya está compartiendo (track activo). false
  // también cubre "el usuario todavía no tocó el botón" — no distingue de
  // "nunca lo intentó" porque no hace falta para la UI actual.
  const [screenSharing, setScreenSharing] = useState(false)
  // true = el usuario canceló el diálogo nativo o denegó el permiso al
  // tocar "Compartir pantalla" — la UI usa esto para mostrar instrucciones,
  // igual que micBlocked/cameraBlocked.
  const [screenShareBlocked, setScreenShareBlocked] = useState(false)

  const sessionRef = useRef<CobrowseSession | null>(null)
  const callRef = useRef<Promise<WebRTCCall> | null>(null)
  const remoteControlEnabledRef = useRef(true)
  const geometryIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopGeometryReporting = useCallback(() => {
    if (geometryIntervalRef.current) {
      clearInterval(geometryIntervalRef.current)
      geometryIntervalRef.current = null
    }
  }, [])

  // ── Limpieza completa de la sesión activa ─────────────────────────────────

  const cleanup = useCallback(() => {
    stopGeometryReporting()
    callRef.current?.then((c) => { c.close(); }).catch(() => { /* ignore */ })
    callRef.current = null
    sessionRef.current?.destroy()
    sessionRef.current = null
    setRemoteStream(null)
    // Vuelven a sus valores por defecto (mic apagado, control remoto
    // permitido) para la PRÓXIMA sesión — no arrastran lo que el usuario
    // haya tocado en la anterior.
    setMuted(true)
    setRemoteControlEnabled(true)
    remoteControlEnabledRef.current = true
    setAgentDisconnected(false)
    setMicBlocked(false)
    setCameraBlocked(false)
    setScreenSharing(false)
    setScreenShareBlocked(false)
  }, [stopGeometryReporting])

  // Detener llamada/compartir pantalla cuando la sesión termina o entra en error
  useEffect(() => {
    if (status === 'ended' || status === 'error') {
      stopGeometryReporting()
      callRef.current?.then((c) => { c.close(); }).catch(() => { /* ignore */ })
      callRef.current = null
      setRemoteStream(null)
      setMuted(true)
      setRemoteControlEnabled(true)
      remoteControlEnabledRef.current = true
      setAgentDisconnected(false)
      setMicBlocked(false)
      setCameraBlocked(false)
      setScreenSharing(false)
      setScreenShareBlocked(false)
      clearPersistedSession()
    }
  }, [status, stopGeometryReporting])

  // Volver a idle automáticamente 3 segundos después de ended/error
  useEffect(() => {
    if (status === 'ended' || status === 'error') {
      const t = setTimeout(() => { setStatus('idle'); }, 3000)
      return () => { clearTimeout(t); }
    }
  }, [status])

  // Cleanup al desmontar el componente que use este hook
  useEffect(() => () => { cleanup(); }, [cleanup])

  // ── Conexión (compartida entre "pedir soporte" y "retomar tras reload") ────

  const connectToSession = useCallback((sessionId: string, reason?: string) => {
    setStatus('connecting')
    setMicBlocked(false)
    setCameraBlocked(false)
    // Solo para decidir si mostrar la guía preventiva ("te vamos a pedir el
    // micrófono") — nunca dispara el prompt del navegador por sí solo.
    void queryMediaPermission('microphone').then(setMicPermissionHint)

    api
      .get<CobrowseTokenResponse>('/cobrowse/token')
      .then(({ data }) => {
        const currentPath = window.location.pathname
        // Se dispara ahora, en paralelo — para cuando el agente se una (más
        // abajo) y arranquemos la llamada, ya suele estar resuelto.
        const turnServersPromise = fetchTurnCredentials(data.wsUrl, data.token)

        const session = new CobrowseSession({
          wsBaseUrl: data.wsUrl,
          token: data.token,
          sessionId,
          userId: '',
          path: currentPath,
          reason,

          onStatusChange: (s) => { setStatus(s); },

          onSessionCreated: () => { persistSession(sessionId); },

          onSessionReady: () => {
            setAgentDisconnected(false)
            // Control remoto ON por defecto — avisar al agente ya mismo,
            // sin esperar a que el usuario toque el switch (ver comentario
            // arriba del hook).
            session.sendControlState(true)

            // Arrancar la llamada de audio (el cliente responde a la oferta
            // del agente, nunca la inicia). Compartir pantalla es una
            // acción MANUAL del usuario (shareScreen(), más abajo) — el
            // navegador exige un gesto reciente, no se puede automatizar.
            callRef.current?.then((c) => { c.close(); }).catch(() => { /* ignore */ })
            callRef.current = createWebRTCCall({
              isInitiator: false,
              sendSignal: (p) => { session.sendWebRTCSignal(p); },
              onRemoteStream: setRemoteStream,
              extraIceServers: turnServersPromise,
            })
          },

          onAgentControl: createRemoteControlHandler(() => remoteControlEnabledRef.current),

          onWebRTCSignal: (payload) => {
            callRef.current?.then((c) => { c.handleSignal(payload); }).catch(() => { /* ignore */ })
          },

          onAgentDisconnected: () => { setAgentDisconnected(true); },
          onAgentReconnected: () => { setAgentDisconnected(false); },
          // sessionId ya está persistido (onSessionCreated de más arriba ya
          // corrió) — el reload de acá abajo dispara el mismo camino de
          // "retomar tras reload" de más abajo en este hook, sin perder la
          // sesión ni que el agente tenga que reconectar nada de su lado.
          // Compartir pantalla NO sobrevive un reload (getDisplayMedia se
          // corta con la página) — el usuario tendrá que volver a tocar
          // "Compartir pantalla" tras recargar, igual que con cámara/mic.
          onReloadPage: () => { window.location.reload(); },

          onSessionEnded: () => {
            stopGeometryReporting()
            callRef.current?.then((c) => { c.close(); }).catch(() => { /* ignore */ })
            callRef.current = null
            setRemoteStream(null)
            clearPersistedSession()
          },

          onError: (msg) => {
            console.warn('[CoBrowse] Error de sesión:', msg)
          },
        })

        sessionRef.current = session
        session.connect()
      })
      .catch(() => {
        console.warn('[CoBrowse] No se pudo obtener token de soporte')
        setStatus('error')
        clearPersistedSession()
      })
  }, [stopGeometryReporting])

  // ── Acción principal: pedir soporte (sesión nueva) ────────────────────────

  const requestSupport = useCallback((reason: string) => {
    if (status !== 'idle') return
    connectToSession(crypto.randomUUID(), reason)
  }, [status, connectToSession])

  // ── Retomar automáticamente tras un reload a mitad de sesión ──────────────
  // Solo una vez al montar — si `eco_events` ya no tiene la sesión (venció
  // el período de gracia), connectToSession igual funciona, solo que
  // arranca de cero como si fuera un requestSupport() más.

  useEffect(() => {
    const persisted = readPersistedSession()
    if (persisted) connectToSession(persisted)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al montar
  }, [])

  // ── Terminar sesión manualmente ───────────────────────────────────────────

  const endSession = useCallback(() => {
    stopGeometryReporting()
    callRef.current?.then((c) => { c.close(); }).catch(() => { /* ignore */ })
    callRef.current = null
    sessionRef.current?.end()
    setStatus('ended')
    clearPersistedSession()
  }, [stopGeometryReporting])

  // ── Control remoto / llamada — acciones expuestas a la UI ─────────────────

  const toggleRemoteControl = useCallback(() => {
    setRemoteControlEnabled((prev) => {
      const next = !prev
      remoteControlEnabledRef.current = next
      sessionRef.current?.sendControlState(next)
      return next
    })
  }, [])

  // Si ya hay track de audio, solo alterna mute/unmute (sin volver a pedir
  // permiso). Si nunca se consiguió (denegado al conectar), este mismo botón
  // hace de "activar micrófono" real — pide el permiso recién ahora, que es
  // el momento en que el usuario lo espera. Sirve también como "Reintentar"
  // desde el modal de micBlocked.
  const toggleMute = useCallback(() => {
    callRef.current
      ?.then(async (c) => {
        if (!c.hasLocalAudio()) {
          const ok = await c.retryAudio()
          if (ok) {
            setMicBlocked(false)
            setMuted(false)
          } else {
            setMicBlocked(true)
          }
          return
        }
        setMicBlocked(false)
        setMuted(c.toggleMute())
      })
      .catch(() => { /* ignore */ })
  }, [])

  const enableVideo = useCallback(() => {
    callRef.current
      ?.then((c) => c.addVideo().then(() => { setCameraBlocked(false); }))
      .catch(() => { setCameraBlocked(true); })
  }, [])

  // Compartir pantalla — acción explícita del usuario (requiere gesto,
  // getDisplayMedia lo exige). Arranca el reporte periódico de geometría de
  // ventana apenas el track está activo, y lo detiene solo cuando el track
  // termina (botón nativo "Dejar de compartir" del navegador, o el usuario
  // cierra la pestaña compartida) — no cuando este componente se re-renderiza.
  const shareScreen = useCallback(() => {
    callRef.current
      ?.then(async (c) => {
        const track = await c.addScreenShare()
        setScreenShareBlocked(false)
        setScreenSharing(true)

        stopGeometryReporting()
        sessionRef.current?.sendScreenGeometry(currentWindowGeometry())
        geometryIntervalRef.current = setInterval(() => {
          sessionRef.current?.sendScreenGeometry(currentWindowGeometry())
        }, GEOMETRY_REPORT_MS)

        track.addEventListener('ended', () => {
          stopGeometryReporting()
          setScreenSharing(false)
        })
      })
      .catch(() => {
        // Usuario canceló el diálogo nativo, o el permiso está denegado a
        // nivel SO (Windows/macOS pueden bloquear captura de pantalla por
        // política) — no hay forma de distinguir ambos casos desde JS.
        setScreenShareBlocked(true)
      })
  }, [stopGeometryReporting])

  return {
    status, requestSupport, endSession,
    remoteControlEnabled, toggleRemoteControl,
    muted, toggleMute, remoteStream, enableVideo,
    screenSharing, screenShareBlocked, shareScreen,
    agentDisconnected,
    micPermissionHint, micBlocked, cameraBlocked,
  }
}
